import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronLeft, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get Chinese period for an hour (bilingual)
const getChinesePeriod = (hour) => {
  const periods = [
    { cn: "子時", en: "Zi" },
    { cn: "丑時", en: "Chou" },
    { cn: "寅時", en: "Yin" },
    { cn: "卯時", en: "Mao" },
    { cn: "辰時", en: "Chen" },
    { cn: "巳時", en: "Si" },
    { cn: "午時", en: "Wu" },
    { cn: "未時", en: "Wei" },
    { cn: "申時", en: "Shen" },
    { cn: "酉時", en: "You" },
    { cn: "戌時", en: "Xu" },
    { cn: "亥時", en: "Hai" }
  ];
  return periods[Math.floor(hour / 2)];
};

// Parse date key to get date components
const parseDateKey = (dateKey) => {
  const parts = dateKey.split('_');
  return {
    year: parseInt(parts[0]),
    month: parseInt(parts[1]),
    day: parseInt(parts[2]),
    hour: parseInt(parts[3])
  };
};

// Format date for display
const formatDate = (year, month, day) => {
  const months = ["January", "February", "March", "April", "May", "June", 
                  "July", "August", "September", "October", "November", "December"];
  return `${months[month - 1]} ${day}, ${year}`;
};

export default function HomePage() {
  const navigate = useNavigate();
  const [chartImages, setChartImages] = useState({});
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [liveTime, setLiveTime] = useState(new Date());

  // Live time update
  useEffect(() => {
    const timer = setInterval(() => {
      setLiveTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch available chart images
  useEffect(() => {
    const fetchChartImages = async () => {
      try {
        const res = await axios.get(`${API}/qimen/lookup/status`);
        const images = res.data.chart_images || {};
        setChartImages(images);
      } catch (err) {
        console.error("Failed to fetch chart images:", err);
      }
    };
    fetchChartImages();
  }, []);

  // Get current chart based on live time
  const currentDateKey = `${liveTime.getFullYear()}_${String(liveTime.getMonth() + 1).padStart(2, '0')}_${String(liveTime.getDate()).padStart(2, '0')}_${String(liveTime.getHours()).padStart(2, '0')}`;
  const currentChartImage = chartImages[currentDateKey] ? `${process.env.REACT_APP_BACKEND_URL}${chartImages[currentDateKey]}` : null;
  
  // For display - use live time
  const currentYear = liveTime.getFullYear();
  const currentMonth = liveTime.getMonth() + 1;
  const currentDay = liveTime.getDate();

  // Live time formatting
  const liveHours = String(liveTime.getHours()).padStart(2, '0');
  const liveMinutes = String(liveTime.getMinutes()).padStart(2, '0');
  const liveChinesePeriod = getChinesePeriod(liveTime.getHours());

  const handleViewChart = () => {
    setShowRegisterModal(true);
  };

  const handleRegister = () => {
    setShowRegisterModal(false);
    if (currentChartImage) {
      const params = new URLSearchParams({
        system: "selectZhiRun",
        year: String(currentYear),
        month: String(currentMonth),
        day: String(currentDay),
        hour: String(liveTime.getHours()),
        minute: "0",
      });
      navigate(`/result?${params.toString()}`);
    }
  };

  return (
    <div className="home-page-new" data-testid="home-page">
      {/* Header */}
      <header className="home-header-new">
        <h1 className="home-title-new">Qi Men Dun Jia</h1>
        <p className="home-tagline">The Most Powerful Tool for QiMen Calculation and Charting</p>
      </header>

      {/* Main Content */}
      <main className="home-main">
        {/* Current Hour Chart Section */}
        <section className="chart-section">
          <div className="chart-header">
            <h2 className="chart-section-title">CURRENT HOUR CHART</h2>
          </div>

          <>
            {/* Date/Time Display */}
              <div className="chart-navigator">
                <Button
                  variant="ghost"
                  size="icon"
                  className="nav-btn"
                  disabled={true}
                  data-testid="prev-chart-btn"
                >
                  <ChevronLeft size={24} />
                </Button>

                <div className="nav-center">
                  <div className="date-display">
                    {formatDate(currentYear, currentMonth, currentDay)}
                  </div>
                  <div className="time-display-static">
                    <span className="hour-display">{liveHours}:{liveMinutes}</span>
                    <span className="chinese-period">{liveChinesePeriod.cn} ({liveChinesePeriod.en})</span>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="nav-btn"
                  disabled={true}
                  data-testid="next-chart-btn"
                >
                  <ChevronRight size={24} />
                </Button>
              </div>

              {/* Chart Preview */}
              {currentChartImage ? (
                <div className="chart-preview" onClick={handleViewChart} data-testid="chart-preview">
                  <div className="chart-image-container">
                    <img src={currentChartImage} alt="Current Hour Chart" className="chart-preview-image" />
                    {/* Register overlay on center palace */}
                    <div className="center-register-overlay">
                      <div className="register-badge">
                        <Lock size={20} />
                        <span>Register to view</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="chart-preview no-chart" data-testid="chart-preview">
                  <div className="no-chart-message">
                    <p>No chart available for current hour</p>
                    <span>Upload a chart to get started</span>
                  </div>
                </div>
              )}

              {/* View Button */}
              <Button
                className="view-chart-btn"
                onClick={handleViewChart}
                disabled={!currentChartImage}
                data-testid="view-chart-btn"
              >
                <Lock size={16} />
                View Full Chart
                <ChevronRight size={18} />
              </Button>
            </>
        </section>
      </main>

      {/* Footer Links */}
      <footer className="home-footer-new">
        <div className="footer-links">
          <Link to="/pricing" className="footer-link pricing-link" data-testid="pricing-link">
            <ChevronRight size={16} />
            View Pricing
          </Link>
        </div>
        <div className="footer-legal">
          <Link to="/terms" className="legal-link">Terms of Conditions</Link>
          <span className="legal-separator">|</span>
          <Link to="/privacy" className="legal-link">Privacy and Policies</Link>
        </div>
        <p className="footer-credit">QMDJ.live - Qi Men Dun Jia Plotter</p>
        <p className="footer-copyright">Copyright © 2026 Steven Yong - Digital Dive Coaching Sdn. Bhd. All rights reserved worldwide.</p>
      </footer>

      {/* Register Modal */}
      {showRegisterModal && (
        <div className="register-modal-overlay" onClick={() => setShowRegisterModal(false)}>
          <div className="register-modal" onClick={(e) => e.stopPropagation()}>
            <div className="register-modal-header">
              <Lock size={32} className="register-icon" />
              <h3>View/Access The Full Features of Qimen</h3>
            </div>
            <p>(Please register with your email)</p>
            <div className="register-modal-buttons">
              <Button className="register-btn" onClick={handleRegister}>
                Continue as Guest
              </Button>
              <Button variant="outline" className="login-btn" onClick={() => setShowRegisterModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
